import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import Select from "react-select";
import {
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaUndo,
  FaShoppingCart,
} from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { useParams, useNavigate } from "react-router-dom";

function ReturnInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch invoice by ID
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}invoices/${id}`);
        if (res.data.success && res.data.data) {
          const inv = res.data.data;
          setInvoice(inv);
          const formatted = inv.items.map((it) => ({
            ...it,
            returnQty: 0,
            reason: "",
            refund: 0,
          }));
          setReturnItems(formatted);
        }
      } catch (err) {
        console.error("Error fetching invoice:", err);
        Swal.fire("Error", "Failed to load invoice data.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  const handleReturnChange = (index, field, value) => {
  const updated = [...returnItems];
  const item = updated[index];

  if (field === "returnQty") {
    let qty = parseFloat(value || 0);
    const soldQty = parseFloat(item.qty);

    // 🚫 Prevent returning more than sold
    if (qty > soldQty) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Quantity",
        text: `You cannot return more than sold quantity (${soldQty}).`,
        timer: 2000,
        showConfirmButton: false,
      });
      qty = soldQty;
    }

    // 🧮 Update refund and value
    const rate = parseFloat(item.amt);
    item.returnQty = qty;
    item.refund = qty * rate;
  } else {
    item[field] = value;
  }

  updated[index] = item;
  setReturnItems(updated);
};


  const totalRefund = returnItems.reduce(
    (sum, it) => sum + (it.refund || 0),
    0
  );

  const handleSubmitReturn = async (e) => {
    e.preventDefault();

    const itemsToReturn = returnItems.filter(
      (it) => parseFloat(it.returnQty) > 0
    );

    if (itemsToReturn.length === 0) {
      Swal.fire("No items selected", "Enter return quantity first.", "warning");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        invoice_id: id,
        customer_name: invoice.full_name,
        phone: invoice.phone,
        date: new Date().toISOString().split("T")[0],
        items: itemsToReturn.map((it) => ({
          invoice_item_id: it.id,
          medicine_id: it.medicine_id,
          medicine_name: it.medicine_name,
          qty_sold: it.qty,
          qty_returned: it.returnQty,
          reason: it.reason,
          refund: it.refund,
        })),
        total_refund: totalRefund,
      };

      const res = await axios.post(`${baseurl}invoices/return`, payload);
      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Return Processed",
          text: "Return invoice generated successfully!",
          timer: 1800,
          showConfirmButton: false,
        });
        navigate("/all-invoice");
      } else {
        Swal.fire("Error", res.data.message || "Return failed", "error");
      }
    } catch (err) {
      console.error("Return error:", err);
      Swal.fire("Error", "Failed to process return", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !invoice) {
    return (
      <>
        <Topbar />
        <Sidebar />
        <main className="main-wrapper text-center py-5">
          <h5>Loading return invoice data...</h5>
        </main>
      </>
    );
  }

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="container py-4">
          <div className="card shadow-lg border-0 rounded-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center rounded-top-4">
              <h4 className="fw-bold d-flex align-items-center gap-2 mb-0">
                <FaUndo className="text-danger" /> Return Items for Invoice #{id}
              </h4>
              <span className="text-muted small">
                Date: {invoice.date} | Mode: {invoice.payment_mode.toUpperCase()}
              </span>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered align-middle text-center">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Medicine</th>
                      <th>Qty Sold</th>
                      <th>Rate (₹)</th>
                      <th>Return Qty</th>
                      <th>Refund (₹)</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnItems.map((item, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="text-start">{item.medicine_name}</td>
                        <td>{item.qty}</td>
                        <td>{item.amt}</td>
                        <td style={{ width: "120px" }}>
                          <input
                            type="number"
                            min="0"
                            max={item.qty}
                            value={item.returnQty}
                            onChange={(e) =>
                              handleReturnChange(i, "returnQty", e.target.value)
                            }
                            className="form-control text-center"
                          />
                        </td>
                        <td className="text-success fw-semibold">
                          ₹{item.refund.toFixed(2)}
                        </td>
                        <td style={{ width: "200px" }}>
                          <input
                            type="text"
                            placeholder="Reason (optional)"
                            value={item.reason}
                            onChange={(e) =>
                              handleReturnChange(i, "reason", e.target.value)
                            }
                            className="form-control"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <h5 className="fw-bold mb-0">
                  Total Refund:{" "}
                  <span className="text-success">₹{totalRefund.toFixed(2)}</span>
                </h5>
                <button
                  className="btn btn-danger btn-lg px-5 py-2"
                  onClick={handleSubmitReturn}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Confirm Return"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ReturnInvoice;
